<template>
  <div class="typing-game" :class="{ collapsed: isStatsCollapsed }">
    <div
      class="game-sidebar"
      v-if="gameStarted"
      :class="{ collapsed: isStatsCollapsed }"
    >
      <div class="collapse-toggle" @click="toggleStats">
        <span class="toggle-icon">{{ isStatsCollapsed ? "◀" : "▶" }}</span>
      </div>

      <div class="stats-container">
        <div class="sidebar-header">
          <h1>{{ title }}</h1>
          <div class="divider"></div>
        </div>

        <div class="stat-box score">
          <div class="stat-icon">🎯</div>
          <div class="stat-content">
            <div class="stat-value">{{ score }}</div>
            <div class="stat-label">得分</div>
          </div>
        </div>
        <div class="stat-box accuracy">
          <div class="stat-icon">✨</div>
          <div class="stat-content">
            <div class="stat-value">{{ accuracy }}%</div>
            <div class="stat-label">正确率</div>
          </div>
        </div>
        <div class="stat-box level">
          <div class="stat-icon">📈</div>
          <div class="stat-content">
            <div class="stat-value">{{ currentLevel }}/{{ levels.length }}</div>
            <div class="stat-label">{{ levelName }}</div>
          </div>
        </div>
        <div class="divider"></div>

        <button class="history-button" @click="showHistory = true">
          <span class="button-icon">📊</span>
          历史记录
        </button>

        <button class="exit-button" @click="exitPractice">
          <span class="button-icon">🚪</span>
          退出练习
        </button>
      </div>
    </div>

    <div class="game-display" :class="{ correct: isCorrect, wrong: isWrong }">
      <div class="progress-container">
        <div class="progress-row">
          <div class="level-info">
            Level {{ currentLevel }} - {{ levelName }}
          </div>
          <div class="progress">
            <div class="progress-bar" :style="{ width: progress + '%' }"></div>
          </div>
        </div>
      </div>

      <div class="target-letters">
        <span
          v-for="(letter, index) in targetLetters"
          :key="index"
          :class="{
            current: index === currentIndex,
            completed: index < currentIndex,
          }"
          >{{ letter === " " ? "␣" : letter }}</span
        >
      </div>

      <div class="finger-hint" v-if="fingerPositions[currentLetter]">
        请用{{ getFinger(fingerPositions[currentLetter].finger) }}
        {{ getRowDirection(fingerPositions[currentLetter].row) }}按键
      </div>
    </div>

    <div class="typing-guide">
      <div class="practice-keyboard">
        <div class="practice-row">
          <div
            v-for="key in [
              '`',
              '1',
              '2',
              '3',
              '4',
              '5',
              '6',
              '7',
              '8',
              '9',
              '0',
              '-',
              '=',
            ]"
            :key="key"
            class="practice-key"
            :class="{ active: currentLetter.toUpperCase() === key }"
          >
            {{ key }}
          </div>
          <div class="practice-key backspace-key">←</div>
        </div>
        <div class="practice-row">
          <div class="practice-key tab-key">Tab</div>
          <div
            v-for="key in [
              'Q',
              'W',
              'E',
              'R',
              'T',
              'Y',
              'U',
              'I',
              'O',
              'P',
              '[',
              ']',
            ]"
            :key="key"
            class="practice-key"
            :class="{ active: currentLetter.toUpperCase() === key }"
          >
            {{ key }}
          </div>
          <div class="practice-key backslash-key">\</div>
        </div>
        <div class="practice-row">
          <div class="practice-key caps-key">Caps</div>
          <div
            v-for="key in [
              'A',
              'S',
              'D',
              'F',
              'G',
              'H',
              'J',
              'K',
              'L',
              ';',
              '\'',
            ]"
            :key="key"
            class="practice-key"
            :class="{ active: currentLetter.toUpperCase() === key }"
          >
            {{ key }}
          </div>
          <div class="practice-key enter-key">Enter</div>
        </div>
        <div class="practice-row">
          <div class="practice-key shift-key">Shift</div>
          <div
            v-for="key in ['Z', 'X', 'C', 'V', 'B', 'N', 'M', ',', '.', '/']"
            :key="key"
            class="practice-key"
            :class="{ active: currentLetter.toUpperCase() === key }"
          >
            {{ key }}
          </div>
          <div class="practice-key shift-key-right">Shift</div>
        </div>
        <div class="practice-row">
          <div class="practice-key ctrl-key">Ctrl</div>
          <div class="practice-key win-key">Win</div>
          <div class="practice-key alt-key">Alt</div>
          <div
            class="practice-key space-key"
            :class="{ active: currentLetter === ' ' }"
          >
            空格
          </div>
          <div class="practice-key alt-key">Alt</div>
          <div class="practice-key win-key">Win</div>
          <div class="practice-key menu-key"></div>
          <div class="practice-key ctrl-key">Ctrl</div>
        </div>
      </div>

      <div class="hands-container">
        <div class="hand left-hand">
          <div class="finger-container">
            <div
              class="finger pinky"
              :class="{ active: fingerMap[currentLetter] === 'LP' }"
            >
              <div
                class="finger-track"
                v-show="fingerMap[currentLetter] === 'LP'"
              ></div>
              小指
            </div>
          </div>
          <div class="finger-container">
            <div
              class="finger ring"
              :class="{ active: fingerMap[currentLetter] === 'LR' }"
            >
              <div
                class="finger-track"
                v-show="fingerMap[currentLetter] === 'LR'"
              ></div>
              无名指
            </div>
          </div>
          <div class="finger-container">
            <div
              class="finger middle"
              :class="{ active: fingerMap[currentLetter] === 'LM' }"
            >
              <div
                class="finger-track"
                v-show="fingerMap[currentLetter] === 'LM'"
              ></div>
              中指
            </div>
          </div>
          <div class="finger-container">
            <div
              class="finger index"
              :class="{ active: fingerMap[currentLetter] === 'LI' }"
            >
              <div
                class="finger-track"
                v-show="fingerMap[currentLetter] === 'LI'"
              ></div>
              食指
            </div>
          </div>
        </div>
        <div class="hand right-hand">
          <div class="finger-container">
            <div
              class="finger index"
              :class="{ active: fingerMap[currentLetter] === 'RI' }"
            >
              <div
                class="finger-track"
                v-show="fingerMap[currentLetter] === 'RI'"
              ></div>
              食指
            </div>
          </div>
          <div class="finger-container">
            <div
              class="finger middle"
              :class="{ active: fingerMap[currentLetter] === 'RM' }"
            >
              <div
                class="finger-track"
                v-show="fingerMap[currentLetter] === 'RM'"
              ></div>
              中指
            </div>
          </div>
          <div class="finger-container">
            <div
              class="finger ring"
              :class="{ active: fingerMap[currentLetter] === 'RR' }"
            >
              <div
                class="finger-track"
                v-show="fingerMap[currentLetter] === 'RR'"
              ></div>
              无名指
            </div>
          </div>
          <div class="finger-container">
            <div
              class="finger pinky"
              :class="{ active: fingerMap[currentLetter] === 'RP' }"
            >
              <div
                class="finger-track"
                v-show="fingerMap[currentLetter] === 'RP'"
              ></div>
              小指
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 添加打字报告弹窗 -->
    <div class="report-modal" v-if="showReport">
      <div class="report-content">
        <h2>{{ title }}学习报告</h2>
        <div class="report-stats">
          <div class="stat-item">
            <div class="stat-label">练习用时</div>
            <div class="stat-value">{{ formatTime(totalTime) }}</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">最终得分</div>
            <div class="stat-value">{{ score }}分</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">正确率</div>
            <div class="stat-value">{{ accuracy }}%</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">打字速度</div>
            <div class="stat-value">{{ typingSpeed }}个/分钟</div>
          </div>
        </div>

        <div class="report-message">
          <div class="result-container">
            <img :src="resultImage" :alt="resultMessage" />
            <p>{{ resultMessage }}</p>
          </div>
        </div>

        <div class="report-buttons">
          <button @click="restartPractice">关闭</button>
        </div>
      </div>
    </div>

    <!-- 添加历史记录弹窗 -->
    <div class="history-modal" v-if="showHistory">
      <div class="history-content">
        <div class="history-header">
          <div class="history-title-icon">📊</div>
          <h2>练习历史记录</h2>
          <button class="history-close-btn" @click="showHistory = false">
            ✖
          </button>
        </div>

        <div class="history-decoration top-left"></div>
        <div class="history-decoration top-right"></div>
        <div class="history-decoration bottom-left"></div>
        <div class="history-decoration bottom-right"></div>

        <div class="history-list" v-if="historyRecords.length">
          <div
            class="history-item"
            v-for="(record, index) in historyRecords"
            :key="index"
          >
            <div class="history-item-header">
              <div class="history-date">
                <span class="date-icon">🗓️</span>
                {{ formatDate(record.date) }}
              </div>
              <div class="history-badge">练习记录 #{{ index + 1 }}</div>
            </div>
            <div class="history-details">
              <div class="detail-item">
                <div class="detail-icon">⏱️</div>
                <span class="label">用时：</span>
                <span class="value">{{ formatTime(record.totalTime) }}</span>
              </div>
              <div class="detail-item">
                <div class="detail-icon">🎯</div>
                <span class="label">得分：</span>
                <span class="value">{{ record.score }}分</span>
              </div>
              <div class="detail-item">
                <div class="detail-icon">✨</div>
                <span class="label">正确率：</span>
                <span class="value">{{ record.accuracy }}%</span>
              </div>
              <div class="detail-item">
                <div class="detail-icon">⚡</div>
                <span class="label">速度：</span>
                <span class="value">{{ record.typingSpeed }}个/分钟</span>
              </div>
            </div>
          </div>
        </div>
        <div class="no-history" v-else>
          <div class="no-history-icon">🔍</div>
          <p>还没有练习记录哦，开始练习吧！</p>
          <div class="no-history-decoration"></div>
        </div>
        <div class="history-buttons">
          <button
            class="history-button clear-button"
            @click="clearHistory"
            v-if="historyRecords.length"
          >
            <span class="button-icon">🗑️</span>
            清空记录
          </button>
          <button
            class="history-button close-button"
            @click="showHistory = false"
          >
            <span class="button-icon">✖️</span>
            关闭
          </button>
        </div>
      </div>
    </div>

    <!-- 添加回到顶部按钮 -->
    <div class="back-to-top" @click="scrollToTop" v-show="showBackToTop">
      <div class="arrow-up">↑</div>
    </div>
  </div>
</template>

<script>
export default {
  name: "TypingPlanetIntroLearn",
  data() {
    return {
      title: "",
      gameStarted: true,
      currentLetter: "",
      score: 0,
      totalAttempts: 0,
      correctAttempts: 0,
      accuracy: 100,
      isCorrect: false,
      isWrong: false,
      currentLevel: 1,
      progress: 0,
      levels: [],
      fingerPositions: {
        A: { finger: "LP", row: "home" },
        S: { finger: "LR", row: "home" },
        D: { finger: "LM", row: "home" },
        F: { finger: "LI", row: "home" },
        G: { finger: "LI", row: "home" },
        H: { finger: "RI", row: "home" },
        J: { finger: "RI", row: "home" },
        K: { finger: "RM", row: "home" },
        L: { finger: "RR", row: "home" },
        ";": { finger: "RP", row: "home" },

        Q: { finger: "LP", row: "top" },
        W: { finger: "LR", row: "top" },
        E: { finger: "LM", row: "top" },
        R: { finger: "LI", row: "top" },
        T: { finger: "LI", row: "top" },
        Y: { finger: "RI", row: "top" },
        U: { finger: "RI", row: "top" },
        I: { finger: "RM", row: "top" },
        O: { finger: "RR", row: "top" },
        P: { finger: "RP", row: "top" },

        Z: { finger: "LP", row: "bottom" },
        X: { finger: "LR", row: "bottom" },
        C: { finger: "LM", row: "bottom" },
        V: { finger: "LI", row: "bottom" },
        B: { finger: "LI", row: "bottom" },
        N: { finger: "RI", row: "bottom" },
        M: { finger: "RI", row: "bottom" },
        ",": { finger: "RM", row: "bottom" },
        ".": { finger: "RR", row: "bottom" },
        "/": { finger: "RP", row: "bottom" },
        " ": { finger: "SP", row: "bottom" },
      },
      fingerMap: {
        Q: "LP",
        A: "LP",
        Z: "LP",
        W: "LR",
        S: "LR",
        X: "LR",
        E: "LM",
        D: "LM",
        C: "LM",
        R: "LI",
        F: "LI",
        V: "LI",
        T: "LI",
        G: "LI",
        B: "LI",
        Y: "RI",
        H: "RI",
        N: "RI",
        U: "RI",
        J: "RI",
        M: "RI",
        I: "RM",
        K: "RM",
        ",": "RM",
        O: "RR",
        L: "RR",
        ".": "RR",
        P: "RP",
        ";": "RP",
        "/": "RP",
        " ": "SP",
      },
      showReport: false,
      startTime: null,
      totalTime: 0,
      typingSpeed: 0,
      resultMessage: "",
      resultImage: "",
      resultImages: {
        excellent: "https://wugecdn.steam.fun/typefun/images/excellent.png",
        good: "https://wugecdn.steam.fun/typefun/images/good.png",
        keepTrying: "https://wugecdn.steam.fun/typefun/images/keep-trying.png",
      },
      showHistory: false,
      historyRecords: [],
      maxHistoryRecords: 10,
      isStatsCollapsed: false,
      targetLetters: "",
      currentIndex: 0,
      showBackToTop: false,
    };
  },
  computed: {
    levelName() {
      return this.levels[this.currentLevel - 1]
        ? this.levels[this.currentLevel - 1].name
        : "";
    },
  },
  methods: {
    restartPractice() {
      this.showReport = false;
      this.gameStarted = true;
      this.score = 0;
      this.correctAttempts = 0;
      this.totalAttempts = 0;
      this.accuracy = 100;
      this.currentLevel = 1;
      this.progress = 0;
      this.isCorrect = false;
      this.isWrong = false;

      this.startTime = new Date();

      this.generateNewLetter();

      window.removeEventListener("keydown", this.handleKeyPress);
      window.addEventListener("keydown", this.handleKeyPress);
    },
    generateNewLetter() {
      const currentLevel = this.levels[this.currentLevel - 1];
      const letters = currentLevel.letters.split("");
      const length = currentLevel.length;

      let newLetters = "";

      for (let i = 0; i < length; i++) {
        const randomIndex = Math.floor(Math.random() * letters.length);
        const letter = letters[randomIndex];
        // 对于空格字符，保持原样，其他字符转为大写
        newLetters += letter === " " ? " " : letter.toUpperCase();
      }

      this.targetLetters = newLetters;
      this.currentIndex = 0;
      this.currentLetter = newLetters[0];
    },
    handleKeyPress(event) {
      if (!this.gameStarted) return;

      // 阻止空格键的默认行为（页面滚动）
      if (event.key === " " || event.code === "Space") {
        event.preventDefault();
      }

      // 定义需要忽略的修饰键列表
      const modifierKeys = [
        "CapsLock",
        "Shift",
        "Control",
        "Alt",
        "Meta",
        "Tab",
        "Escape",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
        "PageUp",
        "PageDown",
      ];

      // 如果按下的是修饰键，直接返回，不计入尝试次数
      if (modifierKeys.includes(event.key)) {
        return;
      }

      const pressedKey = event.key === " " ? " " : event.key.toLowerCase();
      const targetKey =
        this.targetLetters[this.currentIndex] === " "
          ? " "
          : this.targetLetters[this.currentIndex].toLowerCase();
      this.totalAttempts++;

      if (pressedKey === targetKey) {
        this.handleCorrectKey();
      } else {
        this.handleWrongKey();
      }

      this.accuracy = Math.round(
        (this.correctAttempts / this.totalAttempts) * 100
      );
    },
    handleCorrectKey() {
      this.isCorrect = true;
      this.correctAttempts++;
      this.score += 10;

      const audio = new Audio(
        "https://wugecdn.steam.fun/typefun/music/correct.mp3"
      );
      audio.play();

      // 移动到下一个字母
      this.currentIndex++;

      // 如果当前组合已完成
      if (this.currentIndex >= this.targetLetters.length) {
        // 根据当前关卡的字母长度调整进度增加量
        const progressIncrement = 100 / 10; // 每完成一组增加10%的进度
        this.progress += progressIncrement;

        if (this.progress >= 100) {
          this.levelUp();
        } else {
          // 生成新的字母组合
          setTimeout(() => {
            this.generateNewLetter();
          }, 300);
        }
      } else {
        // 继续当前组合的下一个字母
        this.currentLetter = this.targetLetters[this.currentIndex];
      }

      setTimeout(() => {
        this.isCorrect = false;
      }, 300);
    },
    handleWrongKey() {
      this.isWrong = true;
      this.score = Math.max(0, this.score - 5);

      const audio = new Audio(
        "https://wugecdn.steam.fun/typefun/music/fail.mp3"
      );
      audio.play();

      setTimeout(() => {
        this.isWrong = false;
      }, 300);
    },
    levelUp() {
      if (this.currentLevel < this.levels.length) {
        this.showLevelCompleteMessage();

        setTimeout(() => {
          this.currentLevel++;
          this.progress = 0;
          this.generateNewLetter();
        }, 1000);
      } else {
        this.generateReport();
      }
    },
    showLevelCompleteMessage() {
      const levelName = this.levels[this.currentLevel - 1].name;
      const message = document.createElement("div");
      message.className = "level-complete-message";
      message.innerHTML = `
        <div class="message-content">
          <h3>${levelName}完成！</h3>
          <p>准备进入下一关...</p>
        </div>
      `;
      document.body.appendChild(message);

      setTimeout(() => {
        document.body.removeChild(message);
      }, 1000);
    },
    generateReport() {
      window.removeEventListener("keydown", this.handleKeyPress);

      const endTime = new Date();
      this.totalTime = (endTime - this.startTime) / 1000;
      this.typingSpeed = Math.round(
        this.correctAttempts / (this.totalTime / 60)
      );

      const reportData = {
        date: new Date().toISOString(),
        totalTime: this.totalTime,
        score: this.score,
        accuracy: this.accuracy,
        typingSpeed: this.typingSpeed,
      };

      this.saveToHistory(reportData);

      this.completeLevelAndSave();

      if (this.score >= 300 && this.accuracy >= 90) {
        this.resultMessage = "太棒了！你是打字小能手！";
        this.resultImage = this.resultImages.excellent;
      } else if (this.score >= 200 && this.accuracy >= 80) {
        this.resultMessage = "做得不错！继续加油！";
        this.resultImage = this.resultImages.good;
      } else {
        this.resultMessage = "慢慢来，相信你会做得更好！";
        this.resultImage = this.resultImages.keepTrying;
      }

      this.showReport = true;
      this.gameStarted = false;

      this.targetLetters = "";
      this.currentIndex = 0;
      this.currentLetter = "";
    },
    saveToHistory(record) {
      const records = JSON.parse(localStorage.getItem("typingHistory") || "[]");

      records.unshift(record);

      this.historyRecords = records.slice(0, this.maxHistoryRecords);

      localStorage.setItem(
        "typingHistory",
        JSON.stringify(this.historyRecords)
      );
    },
    clearHistory() {
      if (confirm("确定要清空所有记录吗？")) {
        this.historyRecords = [];
        localStorage.removeItem("typingHistory");
      }
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(date.getDate()).padStart(2, "0")} ${String(
        date.getHours()
      ).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
    },
    formatTime(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.round(seconds % 60);
      return `${minutes}分${remainingSeconds}秒`;
    },
    closeReport() {
      this.showReport = false;
      this.$router.back();
    },
    getFinger(finger) {
      const fingerMap = {
        LP: "左小指",
        LR: "左无名指",
        LM: "左中指",
        LI: "左食指",
        RI: "右食指",
        RM: "右中指",
        RR: "右无名指",
        RP: "右小指",
        SP: "两个大拇指",
      };
      return fingerMap[finger];
    },
    getRowDirection(row) {
      const rowMap = {
        home: "",
        top: "上方",
        bottom: "下方",
      };
      return rowMap[row];
    },
    initializeFromRoute() {
      // 从路由参数获取练习键位
      const { letters, title } = this.$route.query;

      if (letters) {
        this.title = title || `${letters}键练习`;

        // 设置练习级别
        this.levels = [
          {
            name: "单键练习",
            letters: letters,
            length: 1,
            description: `学习 ${letters} 键的基本位置`,
          },
          {
            name: "双键练习",
            letters: letters,
            length: 2,
            description: `练习 ${letters} 的双键组合`,
          },
          {
            name: "三键练习",
            letters: letters,
            length: 3,
            description: `练习 ${letters} 的三键组合`,
          },
        ];

        // 重新生成字母
        this.generateNewLetter();
      } else {
        // 如果没有传递参数，使用默认值
        this.title = "w和o练习";
        this.levels = [
          {
            name: "单键练习",
            letters: "wo",
            length: 1,
            description: "学习 w 和 o 键的基本位置",
          },
          {
            name: "双键练习",
            letters: "wo",
            length: 2,
            description: "练习 w 和 o 的双键组合",
          },
          {
            name: "三键练习",
            letters: "wo",
            length: 3,
            description: "练习 w 和 o 的三键组合",
          },
        ];
      }
    },
    completeLevelAndSave() {
      const completedLevels = JSON.parse(
        localStorage.getItem("completedLevels") || "[]"
      );

      // 使用路由名称或自定义ID作为完成标记
      const levelId = this.$route.query.levelId || "default";

      if (!completedLevels.includes(levelId)) {
        completedLevels.push(levelId);
        localStorage.setItem(
          "completedLevels",
          JSON.stringify(completedLevels)
        );
      }

      this.showComplete = true;
    },
    exitPractice() {
      window.removeEventListener("keydown", this.handleKeyPress);
      this.$router.back();
    },
    toggleStats() {
      this.isStatsCollapsed = !this.isStatsCollapsed;
    },
    handleScroll() {
      this.showBackToTop = window.scrollY > 300;
    },
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },
  },
  mounted() {
    this.historyRecords = JSON.parse(
      localStorage.getItem("typingHistory") || "[]"
    );

    // 初始化练习内容
    this.initializeFromRoute();

    window.addEventListener("keydown", this.handleKeyPress);
    this.startTime = new Date();

    // 添加滚动监听
    window.addEventListener("scroll", this.handleScroll);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.handleKeyPress);

    // 移除滚动监听
    window.removeEventListener("scroll", this.handleScroll);
  },
  watch: {
    $route() {
      // 当路由变化时重新初始化
      this.initializeFromRoute();
      this.restartPractice();
    },
    score() {
      const scoreBox = document.querySelector(".stat-box.score .stat-value");
      if (scoreBox) {
        scoreBox.style.animation = "none";
        scoreBox.offsetHeight;
        scoreBox.style.animation = "scoreChange 0.3s ease-out";
      }
    },
  },
};
</script>

<style scoped>

.typing-game {
  margin: 20px;
  transition: padding-right 0.3s ease;
}

.typing-game.collapsed {
  padding-right: 20px;
}

.game-header {
  display: none;
}

.game-sidebar {
  --sidebar-width: 220px;
  position: fixed;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: var(--sidebar-width);
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.8),
    rgba(240, 249, 255, 0.9)
  );
  border-radius: 20px 0 0 20px;
  box-shadow: -5px 0 15px rgba(0, 0, 0, 0.05),
    -2px 0 5px rgba(33, 150, 243, 0.1);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  overflow: hidden;
  z-index: 100;
  border-left: 2px solid rgba(255, 255, 255, 0.8);
  padding-right: 5px;
}

.game-sidebar::before {
  content: "";
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(
      circle at 10% 10%,
      rgba(33, 150, 243, 0.05) 0%,
      transparent 60%
    ),
    radial-gradient(
      circle at 90% 90%,
      rgba(76, 175, 80, 0.05) 0%,
      transparent 60%
    );
  z-index: -1;
  opacity: 0.6;
  animation: bgShift 15s infinite alternate ease-in-out;
}

@keyframes bgShift {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(5deg);
  }
}

.game-sidebar.collapsed {
  width: 30px;
  background: rgba(255, 255, 255, 0.95);
  height: 100vh;
  top: 0;
  transform: translateY(0);
  border-radius: 0;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
}

.collapse-toggle {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 30px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: linear-gradient(
    to right,
    rgba(33, 150, 243, 0.2),
    rgba(33, 150, 243, 0.1)
  );
  border-radius: 0 8px 8px 0;
  transition: all 0.3s ease;
  z-index: 101;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.05);
}

.collapse-toggle::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.2), transparent);
  border-radius: 0 8px 8px 0;
  opacity: 0.5;
}

.collapse-toggle:hover {
  background: linear-gradient(
    to right,
    rgba(33, 150, 243, 0.3),
    rgba(33, 150, 243, 0.2)
  );
  width: 35px;
}

.toggle-icon {
  font-size: 14px;
  color: #2196f3;
  transition: all 0.3s ease;
  text-shadow: 0 0 3px rgba(255, 255, 255, 0.8);
  z-index: 2;
}

.collapse-toggle:hover .toggle-icon {
  transform: scale(1.2);
}

.collapsed .toggle-icon {
  transform: rotate(180deg);
}

.stats-container {
  padding: 20px 15px 15px 35px;
  opacity: 1;
  transform: translateX(0);
  transition: all 0.3s ease;
}

.collapsed .stats-container {
  opacity: 0;
  transform: translateX(100%);
}

.sidebar-header {
  text-align: center;
  padding-bottom: 15px;
  position: relative;
}

.sidebar-header h1 {
  font-size: 22px;
  background: linear-gradient(45deg, #2196f3, #03a9f4);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0;
  font-weight: 700;
  letter-spacing: 0.5px;
  position: relative;
  display: inline-block;
}

.sidebar-header h1::after {
  content: "";
  position: absolute;
  bottom: -5px;
  left: 50%;
  transform: translateX(-50%);
  width: 40px;
  height: 3px;
  background: linear-gradient(to right, #2196f3, #03a9f4);
  border-radius: 3px;
}

.divider {
  height: 2px;
  background: linear-gradient(
    to right,
    transparent,
    rgba(33, 150, 243, 0.5),
    transparent
  );
  margin: 15px 0;
  opacity: 0.5;
  position: relative;
  overflow: hidden;
}

.divider::after {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to right,
    transparent,
    rgba(255, 255, 255, 0.8),
    transparent
  );
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(0%);
  }
  100% {
    transform: translateX(200%);
  }
}

.stats-container {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.stat-box {
  position: relative;
  padding: 12px 15px;
  border-radius: 15px;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.7);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.03), 0 1px 3px rgba(0, 0, 0, 0.02),
    inset 0 1px 1px rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.7);
}

.stat-box:hover {
  transform: translateX(-8px) translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05), 0 3px 5px rgba(0, 0, 0, 0.03),
    inset 0 1px 1px rgba(255, 255, 255, 0.9);
}

.stat-box::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    45deg,
    rgba(255, 255, 255, 0.2),
    rgba(255, 255, 255, 0.4)
  );
  opacity: 0;
  transition: opacity 0.3s;
}

.stat-box:hover::before {
  opacity: 1;
}

.stat-box.score {
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.7),
    rgba(230, 255, 230, 0.7)
  );
  border-left: 3px solid rgba(76, 175, 80, 0.7);
}

.stat-box.accuracy {
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.7),
    rgba(230, 240, 255, 0.7)
  );
  border-left: 3px solid rgba(33, 150, 243, 0.7);
}

.stat-box.speed {
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.7),
    rgba(255, 240, 230, 0.7)
  );
  border-left: 3px solid rgba(255, 152, 0, 0.7);
}

.stat-box.level {
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.7),
    rgba(255, 240, 230, 0.7)
  );
  border-left: 3px solid rgba(255, 152, 0, 0.7);
}

.stat-icon {
  width: 40px;
  height: 40px;
  font-size: 20px;
  border-radius: 12px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.05), inset 0 -2px 0 rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
  animation: iconFloat 3s infinite ease-in-out;
}

@keyframes iconFloat {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-3px);
  }
}

.score .stat-icon {
  background: linear-gradient(135deg, #4caf50, #8bc34a);
  color: white;
}

.accuracy .stat-icon {
  background: linear-gradient(135deg, #2196f3, #03a9f4);
  color: white;
}

.speed .stat-icon {
  background: linear-gradient(135deg, #ff9800, #ffc107);
  color: white;
}

.stat-icon::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 50%;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px 12px 0 0;
}

.stat-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.stat-content .stat-value {
  font-size: 26px;
  font-weight: 700;
  white-space: nowrap;
  text-align: center;
  margin-bottom: 2px;
  background: linear-gradient(45deg, #333, #555);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  display: block;
  letter-spacing: 0.5px;
  position: relative;
}

.score .stat-value {
  background: linear-gradient(45deg, #2e7d32, #43a047);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.accuracy .stat-value {
  background: linear-gradient(45deg, #1565c0, #1976d2);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.speed .stat-value {
  background: linear-gradient(45deg, #e65100, #f57c00);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.stat-content .stat-label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  opacity: 0.8;
  text-align: center;
  font-weight: 500;
  color: #666;
}

.stat-content .stat-label::before {
  display: none;
}

.stat-label {
  font-size: 15px;
  color: #666;
  position: relative;
  display: inline-block;
  font-weight: 600;
  margin-top: 10px;
}

.stat-label::before {
  content: "";
  position: absolute;
  top: -4px;
  left: 50%;
  transform: translateX(-50%);
  width: 30px;
  height: 2px;
  background: #ddd;
  border-radius: 2px;
}

/* 游戏显示区域样式优化 */
.game-display {
  margin-top: 30px;
  text-align: center;
  padding: 35px 40px 40px;
  margin: 20px 0;
  border-radius: 20px;
  background: white;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.08), 0 5px 15px rgba(0, 0, 0, 0.04);
  border: 2px solid rgba(25, 118, 210, 0.1);
  position: relative;
  overflow: hidden;
}

.game-display::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at 50% 0%, rgba(200, 230, 255, 0.3), transparent 70%);
  pointer-events: none;
  z-index: 0;
}

.game-display::after {
  content: '';
  position: absolute;
  bottom: -10px;
  right: -10px;
  width: 200px;
  height: 200px;
  background: radial-gradient(circle, rgba(33, 150, 243, 0.05), transparent 70%);
  border-radius: 50%;
  z-index: 0;
}

.game-display.correct {
  background: linear-gradient(to bottom, #e8f5e9, white);
  border-color: #A5D6A7;
  box-shadow: 0 15px 35px rgba(76, 175, 80, 0.15), 0 5px 15px rgba(76, 175, 80, 0.08);
}

.game-display.wrong {
  background: linear-gradient(to bottom, #ffebee, white);
  border-color: #FFCDD2;
  box-shadow: 0 15px 35px rgba(244, 67, 54, 0.15), 0 5px 15px rgba(244, 67, 54, 0.08);
}

.progress-container {
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px dashed rgba(0, 0, 0, 0.05);
  position: relative;
  z-index: 1;
}

.progress-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.level-info {
  font-size: 1.2em;
  color: #546E7A;
  white-space: nowrap;
  flex-shrink: 0;
  font-weight: 600;
  background: #EEF2F6;
  padding: 8px 16px;
  border-radius: 50px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
}

.level-info::before {
  content: "🚀";
  margin-right: 8px;
  font-size: 1.1em;
}

.progress {
  height: 12px;
  background: #EEF2F6;
  border-radius: 6px;
  overflow: hidden;
  flex-grow: 1;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);
  position: relative;
}

.progress::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: repeating-linear-gradient(
    45deg,
    transparent,
    transparent 10px,
    rgba(255, 255, 255, 0.1) 10px,
    rgba(255, 255, 255, 0.1) 20px
  );
  border-radius: 6px;
  z-index: 1;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #4CAF50, #8BC34A);
  transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  z-index: 2;
  box-shadow: 0 0 10px rgba(76, 175, 80, 0.4);
}

.game-display.correct .progress-bar {
  background: linear-gradient(90deg, #4CAF50, #8BC34A);
  box-shadow: 0 0 15px rgba(76, 175, 80, 0.5);
}

.game-display.wrong .progress-bar {
  background: linear-gradient(90deg, #F44336, #FF9800);
  box-shadow: 0 0 15px rgba(244, 67, 54, 0.5);
}

.target-letters {
  font-size: 48px;
  font-weight: bold;
  margin-bottom: 20px;
  letter-spacing: 10px;
}

.target-letters span {
  display: inline-block;
  padding: 0 10px;
  color: #666;
  min-width: 20px;
}

.target-letters span.current {
  color: #2196f3;
  border-bottom: 3px solid #2196f3;
  animation: pulse 1s infinite;
}

.target-letters span.completed {
  color: #4caf50;
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
  100% {
    opacity: 1;
  }
}

.finger-hint {
  font-size: 18px;
  color: #607D8B;
  margin: 15px 0;
  padding: 12px 20px;
  background: rgba(240, 248, 255, 0.7);
  border-radius: 12px;
  display: inline-block;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px dashed rgba(33, 150, 243, 0.3);
  position: relative;
  z-index: 1;
  animation: float 3s infinite ease-in-out;
}

.finger-hint::before {
  content: "👆";
  margin-right: 8px;
  font-size: 20px;
}

@keyframes float {
  0% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
  100% { transform: translateY(0); }
} 

.letter-instruction {
  font-size: 24px;
  color: #666;
  margin: 20px 0;
}

.typing-guide {
  margin: 30px auto;
  background: #ffffff;
  padding: 20px;
  border-radius: 10px;
  border: 1px solid #f0f0f0;
  box-shadow: none;
}

.hands-container {
  display: flex;
  justify-content: center;
  gap: 120px;
  padding: 20px 0;
  perspective: 1000px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 15px;
  box-shadow: none;
  border: 1px solid #f0f0f0;
}

.hand {
  display: flex;
  gap: 12px;
  transform-style: preserve-3d;
  transition: transform 0.3s ease;
}

.hand.left-hand {
  transform: rotateY(-10deg);
}

.hand.right-hand {
  transform: rotateY(10deg);
}

.finger-container {
  position: relative;
  height: 120px;
  display: flex;
  align-items: flex-end;
}

.finger {
  position: relative;
  z-index: 2;
  width: 45px;
  height: 90px;
  background: linear-gradient(to bottom, #ffdbac 0%, #f1c27d 100%);
  border-radius: 22px 22px 12px 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  writing-mode: vertical-rl;
  text-orientation: upright;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
  transition: all 0.3s;
  box-shadow: inset 0 -10px 10px rgba(0, 0, 0, 0.05),
    inset 2px 0 5px rgba(255, 255, 255, 0.3),
    inset -2px 0 5px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.finger::before {
  content: "";
  position: absolute;
  top: 5px;
  left: 50%;
  transform: translateX(-50%);
  width: 25px;
  height: 15px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 50%;
}

.finger::after {
  content: "";
  position: absolute;
  bottom: 5px;
  left: 50%;
  transform: translateX(-50%);
  width: 25px;
  height: 10px;
  background: rgba(241, 194, 125, 0.8);
  border-radius: 10px 10px 5px 5px;
}

.finger.active {
  background: linear-gradient(to bottom, #ffdbac 0%, #e6b26a 100%);
  color: rgba(0, 0, 0, 0.8);
  transform: translateY(-15px);
  box-shadow: inset 0 -10px 10px rgba(0, 0, 0, 0.1),
    inset 2px 0 5px rgba(255, 255, 255, 0.3),
    inset -2px 0 5px rgba(0, 0, 0, 0.2), 0 5px 10px rgba(0, 0, 0, 0.1);
  animation: fingerPulse 0.8s infinite alternate;
}

@keyframes fingerPulse {
  0% {
    transform: translateY(-15px) scale(1);
    box-shadow: inset 0 -10px 10px rgba(0, 0, 0, 0.1),
      inset 2px 0 5px rgba(255, 255, 255, 0.3),
      inset -2px 0 5px rgba(0, 0, 0, 0.2), 0 5px 10px rgba(0, 0, 0, 0.1);
  }
  100% {
    transform: translateY(-15px) scale(1.05);
    box-shadow: inset 0 -10px 10px rgba(0, 0, 0, 0.1),
      inset 2px 0 5px rgba(255, 255, 255, 0.3),
      inset -2px 0 5px rgba(0, 0, 0, 0.2), 0 8px 15px rgba(0, 0, 0, 0.15),
      0 0 8px rgba(76, 175, 80, 0.5);
  }
}

.left-hand .finger.active {
  transform: translateY(-15px) rotate(-2deg);
  animation: fingerPulseLeft 0.8s infinite alternate;
}

.right-hand .finger.active {
  transform: translateY(-15px) rotate(2deg);
  animation: fingerPulseRight 0.8s infinite alternate;
}

@keyframes fingerPulseLeft {
  0% {
    transform: translateY(-15px) rotate(-2deg) scale(1);
    box-shadow: inset 0 -10px 10px rgba(0, 0, 0, 0.1),
      inset 2px 0 5px rgba(255, 255, 255, 0.3),
      inset -2px 0 5px rgba(0, 0, 0, 0.2), 0 5px 10px rgba(0, 0, 0, 0.1);
  }
  100% {
    transform: translateY(-15px) rotate(-2deg) scale(1.05);
    box-shadow: inset 0 -10px 10px rgba(0, 0, 0, 0.1),
      inset 2px 0 5px rgba(255, 255, 255, 0.3),
      inset -2px 0 5px rgba(0, 0, 0, 0.2), 0 8px 15px rgba(0, 0, 0, 0.15),
      0 0 8px rgba(76, 175, 80, 0.5);
  }
}

@keyframes fingerPulseRight {
  0% {
    transform: translateY(-15px) rotate(2deg) scale(1);
    box-shadow: inset 0 -10px 10px rgba(0, 0, 0, 0.1),
      inset 2px 0 5px rgba(255, 255, 255, 0.3),
      inset -2px 0 5px rgba(0, 0, 0, 0.2), 0 5px 10px rgba(0, 0, 0, 0.1);
  }
  100% {
    transform: translateY(-15px) rotate(2deg) scale(1.05);
    box-shadow: inset 0 -10px 10px rgba(0, 0, 0, 0.1),
      inset 2px 0 5px rgba(255, 255, 255, 0.3),
      inset -2px 0 5px rgba(0, 0, 0, 0.2), 0 8px 15px rgba(0, 0, 0, 0.15),
      0 0 8px rgba(76, 175, 80, 0.5);
  }
}

.finger-track {
  position: absolute;
  bottom: 100%;
  left: 50%;
  width: 2px;
  height: 50px;
  background: linear-gradient(to top, rgba(76, 175, 80, 0.5), transparent);
  transform-origin: bottom center;
  animation: moveTrack 0.8s infinite ease-in-out;
}

@keyframes moveTrack {
  0% {
    transform: translateY(0) scaleY(1);
    opacity: 1;
  }
  50% {
    transform: translateY(-20px) scaleY(0.8);
    opacity: 0.5;
  }
  100% {
    transform: translateY(-40px) scaleY(0.6);
    opacity: 0;
  }
}

.finger.pinky {
  height: 75px;
  width: 38px;
  border-bottom: 4px solid rgba(255, 64, 129, 0.5);
}
.finger.ring {
  height: 85px;
  width: 42px;
  border-bottom: 4px solid rgba(124, 77, 255, 0.5);
}
.finger.middle {
  height: 90px;
  width: 45px;
  border-bottom: 4px solid rgba(0, 188, 212, 0.5);
}
.finger.index {
  height: 85px;
  width: 42px;
  border-bottom: 4px solid rgba(255, 193, 7, 0.5);
}

.left-hand .finger {
  transform-origin: bottom right;
  transform: rotate(-5deg);
}

.right-hand .finger {
  transform-origin: bottom left;
  transform: rotate(5deg);
}

/* 键盘显示区域 */
.practice-keyboard {
  background: #2c3e50;
  padding: 20px;
  border-radius: 12px;
  margin: 25px auto;
  width: fit-content;
  transform: none;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  border: 1px solid #1e2b38;
}

.practice-row {
  display: flex;
  gap: 5px;
  margin-bottom: 5px;
  position: relative;
}

/* 错开排列，模拟真实键盘 */
.practice-row:nth-child(2) {
  padding-left: 12px;
}

.practice-row:nth-child(3) {
  padding-left: 18px;
}

.practice-row:nth-child(4) {
  padding-left: 25px;
}

.practice-key {
  width: 40px;
  height: 40px;
  background: #34495e;
  color: white;
  border: none;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 16px;
  box-shadow: 0 3px 0 #1e2b38;
  transition: all 0.15s ease;
  position: relative;
  overflow: hidden;
}

.practice-key::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 40%;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0.12),
    rgba(255, 255, 255, 0)
  );
  border-radius: 5px 5px 0 0;
}

.practice-key.active {
  background: #2196f3;
  transform: translateY(3px);
  box-shadow: 0 0 0 #1e2b38;
}

/* 特殊键位的宽度 */
.backspace-key {
  width: 70px;
}

.tab-key {
  width: 60px;
}

.backslash-key {
  width: 50px;
}

.caps-key {
  width: 70px;
}

.enter-key {
  width: 75px;
}

.shift-key {
  width: 90px;
}

.shift-key-right {
  width: 90px;
}

.ctrl-key,
.alt-key,
.win-key,
.menu-key {
  width: 50px;
}

.space-key {
  width: 250px;
  font-size: 14px;
}

/* 练习报告弹窗优化 */
.report-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(8px);
  animation: fadeIn 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.report-content {
  background: #ffffff;
  padding: 0;
  border-radius: 24px;
  max-width: 600px;
  width: 90%;
  text-align: center;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  animation: bounceIn 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border: none;
  position: relative;
}

@keyframes bounceIn {
  0% {
    transform: scale(0.8);
    opacity: 0;
  }
  70% {
    transform: scale(1.05);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.report-content h2 {
  font-size: 28px;
  margin: 0;
  padding: 25px 0;
  color: #ffffff;
  background: linear-gradient(135deg, #4a6fff, #9c4aff);
  border-radius: 24px 24px 0 0;
  position: relative;
  font-weight: 700;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.report-content h2::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 4px;
  background: #ffffff;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.report-stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin: 30px;
  position: relative;
}

.report-stats::before {
  content: "";
  position: absolute;
  top: -10px;
  left: -10px;
  right: -10px;
  bottom: -10px;
  background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><circle cx="10" cy="10" r="2" fill="%23f0f0f0"/><circle cx="30" cy="10" r="2" fill="%23f0f0f0"/><circle cx="50" cy="10" r="2" fill="%23f0f0f0"/><circle cx="70" cy="10" r="2" fill="%23f0f0f0"/><circle cx="90" cy="10" r="2" fill="%23f0f0f0"/><circle cx="10" cy="30" r="2" fill="%23f0f0f0"/><circle cx="30" cy="30" r="2" fill="%23f0f0f0"/><circle cx="50" cy="30" r="2" fill="%23f0f0f0"/><circle cx="70" cy="30" r="2" fill="%23f0f0f0"/><circle cx="90" cy="30" r="2" fill="%23f0f0f0"/><circle cx="10" cy="50" r="2" fill="%23f0f0f0"/><circle cx="30" cy="50" r="2" fill="%23f0f0f0"/><circle cx="50" cy="50" r="2" fill="%23f0f0f0"/><circle cx="70" cy="50" r="2" fill="%23f0f0f0"/><circle cx="90" cy="50" r="2" fill="%23f0f0f0"/><circle cx="10" cy="70" r="2" fill="%23f0f0f0"/><circle cx="30" cy="70" r="2" fill="%23f0f0f0"/><circle cx="50" cy="70" r="2" fill="%23f0f0f0"/><circle cx="70" cy="70" r="2" fill="%23f0f0f0"/><circle cx="90" cy="70" r="2" fill="%23f0f0f0"/><circle cx="10" cy="90" r="2" fill="%23f0f0f0"/><circle cx="30" cy="90" r="2" fill="%23f0f0f0"/><circle cx="50" cy="90" r="2" fill="%23f0f0f0"/><circle cx="70" cy="90" r="2" fill="%23f0f0f0"/><circle cx="90" cy="90" r="2" fill="%23f0f0f0"/></svg>');
  z-index: -1;
  opacity: 0.3;
}

.stat-item {
  padding: 20px 15px;
  background: #f8f9ff;
  border-radius: 16px;
  transition: all 0.3s ease;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
  border: 2px solid #f0f0f0;
  animation: fadeInUp 0.5s ease-out forwards;
  opacity: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

@keyframes fadeInUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.stat-item:nth-child(1) {
  animation-delay: 0.1s;
}
.stat-item:nth-child(2) {
  animation-delay: 0.2s;
}
.stat-item:nth-child(3) {
  animation-delay: 0.3s;
}
.stat-item:nth-child(4) {
  animation-delay: 0.4s;
}

.stat-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
  border-color: #e0e7ff;
  background: #f0f7ff;
}

.stat-item::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 6px;
  background: linear-gradient(90deg, #4a6fff, #9c4aff);
  opacity: 0;
  transition: opacity 0.3s;
  border-radius: 3px 3px 0 0;
}

.stat-item:hover::before {
  opacity: 1;
}

.stat-label {
  font-size: 15px;
  color: #666;
  margin-bottom: 10px;
  position: relative;
  display: inline-block;
  font-weight: 600;
  text-align: center;
}

.stat-label::before {
  content: "";
  position: absolute;
  bottom: -4px;
  left: 50%;
  transform: translateX(-50%);
  width: 30px;
  height: 2px;
  background: #ddd;
  border-radius: 2px;
}

.stat-value {
  font-size: 26px;
  font-weight: 700;
  color: #4a6fff;
  text-shadow: 0 2px 4px rgba(74, 111, 255, 0.2);
  position: relative;
  display: inline-block;
  text-align: center;
  margin-top: 5px;
}

.report-message {
  margin: 0 30px 30px;
  padding: 25px 20px;
  background: #f0f7ff;
  border-radius: 16px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.05);
  border: 2px dashed #e0e7ff;
  animation: fadeIn 0.6s ease-out 0.5s forwards;
  opacity: 0;
  text-align: center;
}

.report-message::before {
  content: "";
  position: absolute;
  top: -20px;
  right: -20px;
  width: 80px;
  height: 80px;
  background: #4a6fff;
  opacity: 0.1;
  border-radius: 50%;
}

.report-message::after {
  content: "";
  position: absolute;
  bottom: -30px;
  left: -30px;
  width: 100px;
  height: 100px;
  background: #9c4aff;
  opacity: 0.1;
  border-radius: 50%;
}

.report-message .result-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.report-message img {
  width: 120px;
  height: 120px;
  margin-bottom: 15px;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.15));
  animation: pulse 2s infinite ease-in-out;
  transform-origin: center;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

.report-message p {
  font-size: 20px;
  color: #4a6fff;
  font-weight: 600;
  margin: 10px 0 0;
  padding: 0;
  position: relative;
  display: block;
  text-align: center;
}

.report-buttons {
  display: flex;
  gap: 15px;
  justify-content: center;
  padding: 0 30px 30px;
  animation: fadeIn 0.6s ease-out 0.7s forwards;
  opacity: 0;
}

.report-buttons button {
  padding: 12px 25px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 50px;
  transition: all 0.3s ease;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  border: none;
  cursor: pointer;
  min-width: 140px;
}

.report-buttons button:first-child {
  background: linear-gradient(135deg, #4a6fff, #9c4aff);
  color: white;
}

.report-buttons button:first-child:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 20px rgba(74, 111, 255, 0.3);
}

.report-buttons button:first-child:active {
  transform: translateY(-1px);
  box-shadow: 0 5px 10px rgba(74, 111, 255, 0.2);
}

.report-buttons button:last-child {
  background: white;
  color: #666;
  border: 2px solid #eee;
}

.report-buttons button:last-child:hover {
  background: #f8f9ff;
  transform: translateY(-3px);
  box-shadow: 0 12px 20px rgba(0, 0, 0, 0.05);
  border-color: #e0e7ff;
  color: #4a6fff;
}

.report-buttons button:last-child:active {
  transform: translateY(-1px);
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.03);
}

.report-buttons button::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0));
  transform: translateY(-100%);
  transition: transform 0.6s;
}

.report-buttons button:hover::before {
  transform: translateY(0);
}

.history-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease-out;
  backdrop-filter: blur(5px);
}

.history-content {
  background: linear-gradient(145deg, #ffffff, #f0f4ff);
  padding: 30px;
  border-radius: 24px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  position: relative;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15),
    0 5px 15px rgba(66, 133, 244, 0.1), inset 0 -4px 0 rgba(0, 0, 0, 0.05);
  border: 3px solid rgba(255, 255, 255, 0.7);
  animation: popIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  display: flex;
  flex-direction: column;
}

.history-header {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 25px;
  position: relative;
}

.history-title-icon {
  font-size: 32px;
  margin-right: 15px;
  animation: floatIcon 3s infinite ease-in-out;
}

.history-close-btn {
  position: absolute;
  right: 0;
  top: 0;
  background: none;
  border: none;
  font-size: 20px;
  color: #666;
  cursor: pointer;
  padding: 5px;
  transition: all 0.3s ease;
}

.history-close-btn:hover {
  color: #f44336;
  transform: scale(1.2);
}

.history-content h2 {
  font-size: 28px;
  color: #4a4a4a;
  margin: 0;
  background: linear-gradient(90deg, #7b68ee, #3498db);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  font-weight: 700;
  letter-spacing: 0.5px;
}

.history-decoration {
  position: absolute;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  z-index: 0;
  opacity: 0.1;
}

.history-decoration.top-left {
  top: -20px;
  left: -20px;
  background: radial-gradient(circle, #ff9ff3, #feca57);
}

.history-decoration.top-right {
  top: -30px;
  right: -30px;
  width: 100px;
  height: 100px;
  background: radial-gradient(circle, #48dbfb, #1dd1a1);
}

.history-decoration.bottom-left {
  bottom: -40px;
  left: -20px;
  width: 120px;
  height: 120px;
  background: radial-gradient(circle, #ff6b6b, #ee5253);
}

.history-decoration.bottom-right {
  bottom: -30px;
  right: -30px;
  background: radial-gradient(circle, #54a0ff, #2e86de);
}

.history-list {
  margin: 20px 0;
  position: relative;
  z-index: 1;
  overflow-y: auto;
  max-height: calc(80vh - 180px);
  padding-right: 10px;
}

/* 自定义滚动条样式 */
.history-list::-webkit-scrollbar {
  width: 8px;
}

.history-list::-webkit-scrollbar-track {
  background: rgba(240, 240, 255, 0.5);
  border-radius: 10px;
}

.history-list::-webkit-scrollbar-thumb {
  background: rgba(108, 92, 231, 0.3);
  border-radius: 10px;
}

.history-list::-webkit-scrollbar-thumb:hover {
  background: rgba(108, 92, 231, 0.5);
}

.history-item {
  background: rgba(255, 255, 255, 0.8);
  border-radius: 16px;
  padding: 18px;
  margin-bottom: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05), inset 0 -2px 0 rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(230, 230, 250, 0.7);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.history-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1), inset 0 -2px 0 rgba(0, 0, 0, 0.05);
}

.history-item::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(to bottom, #a29bfe, #6c5ce7);
  border-radius: 4px 0 0 4px;
}

.history-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px dashed rgba(108, 92, 231, 0.2);
}

.history-date {
  font-size: 14px;
  color: #666;
  display: flex;
  align-items: center;
}

.date-icon {
  margin-right: 5px;
}

.history-badge {
  background: linear-gradient(145deg, #a29bfe, #6c5ce7);
  color: white;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  box-shadow: 0 2px 5px rgba(108, 92, 231, 0.3);
}

.history-details {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
}

.detail-item {
  display: flex;
  align-items: center;
  padding: 8px;
  background: rgba(240, 240, 255, 0.5);
  border-radius: 10px;
  transition: all 0.3s ease;
}

.detail-item:hover {
  background: rgba(230, 230, 250, 0.8);
  transform: translateY(-2px);
}

.detail-icon {
  font-size: 18px;
  margin-right: 8px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.detail-item .label {
  color: #666;
  font-size: 13px;
  margin-right: auto;
}

.detail-item .value {
  font-weight: 600;
  color: #6c5ce7;
  font-size: 15px;
  background: linear-gradient(90deg, #6c5ce7, #a29bfe);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.no-history {
  text-align: center;
  color: #666;
  padding: 40px 20px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 16px;
  position: relative;
  overflow: hidden;
}

.no-history-icon {
  font-size: 48px;
  margin-bottom: 15px;
  animation: pulse 2s infinite ease-in-out;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

.no-history p {
  font-size: 16px;
  color: #666;
  margin: 0;
}

.no-history-decoration {
  position: absolute;
  bottom: -20px;
  right: -20px;
  width: 100px;
  height: 100px;
  background: radial-gradient(
    circle,
    rgba(108, 92, 231, 0.1),
    rgba(162, 155, 254, 0.05)
  );
  border-radius: 50%;
  z-index: -1;
}

.history-buttons {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 25px;
  position: sticky;
  bottom: 0;
  padding: 15px 0 5px 0;
  z-index: 10;
}

.history-button {
  padding: 12px 24px;
  font-size: 15px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 500;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1), inset 0 -2px 0 rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
}

.history-button::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transition: all 0.5s;
}

.history-button:hover::before {
  left: 100%;
}

.history-button:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.15), inset 0 -2px 0 rgba(0, 0, 0, 0.1);
}

.history-button:active {
  transform: translateY(1px);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1), inset 0 2px 2px rgba(0, 0, 0, 0.1);
}

.clear-button {
  background: linear-gradient(145deg, #ff6b6b, #ee5253);
  color: white;
}

.clear-button:hover {
  background: linear-gradient(145deg, #ee5253, #ff6b6b);
}

.close-button {
  background: linear-gradient(145deg, #a29bfe, #6c5ce7);
  color: white;
}

.close-button:hover {
  background: linear-gradient(145deg, #6c5ce7, #a29bfe);
}

/* 退出按钮样式 */
.exit-button {
  width: 100%;
  padding: 12px;
  background: linear-gradient(135deg, #ff5252, #ff1744);
  color: white;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 10px rgba(255, 82, 82, 0.2),
    inset 0 -2px 0 rgba(0, 0, 0, 0.1);
  margin-top: 5px;
}

.exit-button::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 40%;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px 12px 0 0;
}

.exit-button:hover {
  background: linear-gradient(135deg, #ff1744, #d50000);
  transform: translateY(-3px);
  box-shadow: 0 6px 15px rgba(255, 82, 82, 0.3),
    inset 0 -2px 0 rgba(0, 0, 0, 0.2);
}

.exit-button:active {
  transform: translateY(0);
  box-shadow: 0 2px 5px rgba(255, 82, 82, 0.2),
    inset 0 1px 2px rgba(0, 0, 0, 0.2);
}

.button-icon {
  font-size: 16px;
  position: relative;
  z-index: 2;
}

@keyframes scoreChange {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}

/* 深色模式支持 */
@media (prefers-color-scheme: dark) {
  .game-sidebar {
    background: linear-gradient(
      135deg,
      rgba(44, 62, 80, 0.9),
      rgba(25, 42, 60, 0.95)
    );
    border-left: 2px solid rgba(255, 255, 255, 0.1);
  }

  .game-sidebar::before {
    background: radial-gradient(
        circle at 10% 10%,
        rgba(33, 150, 243, 0.1) 0%,
        transparent 60%
      ),
      radial-gradient(
        circle at 90% 90%,
        rgba(76, 175, 80, 0.1) 0%,
        transparent 60%
      );
  }

  .game-sidebar.collapsed {
    background: rgba(25, 42, 60, 0.95);
  }

  .collapse-toggle {
    background: linear-gradient(
      to right,
      rgba(33, 150, 243, 0.3),
      rgba(33, 150, 243, 0.2)
    );
  }

  .collapse-toggle:hover {
    background: linear-gradient(
      to right,
      rgba(33, 150, 243, 0.4),
      rgba(33, 150, 243, 0.3)
    );
  }

  .sidebar-header h1 {
    background: linear-gradient(45deg, #64b5f6, #42a5f5);
    -webkit-background-clip: text;
    background-clip: text;
  }

  .divider {
    background: linear-gradient(
      to right,
      transparent,
      rgba(33, 150, 243, 0.3),
      transparent
    );
  }

  .stat-box {
    background: rgba(44, 62, 80, 0.7);
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.1),
      inset 0 1px 1px rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.05);
  }

  .stat-box.score {
    background: linear-gradient(
      135deg,
      rgba(44, 62, 80, 0.7),
      rgba(38, 60, 38, 0.7)
    );
  }

  .stat-box.accuracy {
    background: linear-gradient(
      135deg,
      rgba(44, 62, 80, 0.7),
      rgba(38, 50, 60, 0.7)
    );
  }

  .stat-box.speed {
    background: linear-gradient(
      135deg,
      rgba(44, 62, 80, 0.7),
      rgba(60, 50, 38, 0.7)
    );
  }

  .stat-icon {
    background: rgba(25, 42, 60, 0.9);
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.1), inset 0 -2px 0 rgba(0, 0, 0, 0.1);
  }

  .stat-content .stat-value {
    background: linear-gradient(45deg, #b0bec5, #90a4ae);
    -webkit-background-clip: text;
    background-clip: text;
  }

  .score .stat-value {
    background: linear-gradient(45deg, #81c784, #a5d6a7);
    -webkit-background-clip: text;
    background-clip: text;
  }

  .accuracy .stat-value {
    background: linear-gradient(45deg, #64b5f6, #90caf9);
    -webkit-background-clip: text;
    background-clip: text;
  }

  .speed .stat-value {
    background: linear-gradient(45deg, #ffb74d, #ffcc80);
    -webkit-background-clip: text;
    background-clip: text;
  }

  .stat-content .stat-label {
    color: #b0bec5;
  }
}

@keyframes scoreChange {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}
</style>
